import axios from 'axios';
import _ from 'lodash';
import * as Sentry from '@sentry/browser';
import { signOut, getIdToken } from '@/utils/Auth';
import { redirectToLogin } from '@/utils/Router';
import store from '@/store';

const unauthorizedStatus = [ 401 ];
const ignoredStatus = [ 404, 410, 417 ];

export function configAxios() {
  axios.interceptors.request.use(
    (config) => {
      store.dispatch('appState/startLoader');

      // Automatically authorize request headers with session data
      const token = getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return Promise.resolve(config);
    },
    (error) => {
      store.dispatch('appState/stopLoader');

      return Promise.reject(error);
    },
  );

  // Add a response interceptor
  axios.interceptors.response.use(
    (response) => {

      // Any status code that lie within the range of 2xx cause this function to trigger
      store.dispatch('appState/stopLoader');

      return Promise.resolve(response);
    }, (error) => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      store.dispatch('appState/stopLoader');

      // Checks if HTTP Status code should be ignored, and not further processed.
      if (ignoredStatus.includes(error.response.status)) {
        return Promise.reject(error);
      }

      Sentry.withScope(function(scope) {
        const URL = parseURL(error.config.url);
        // group errors together based on their method, URL and response status
        scope.setFingerprint([error.config.method, URL, error.response.status, process.env.NODE_ENV]);
        Sentry.setExtra('stack', error.stack);

        let request: any = {};
        if (_.get(error, 'response.config.url')) {
          request = {
            method: error.config.method,
            url: error.config.url,
            status: error.response.status,
            error: error.toString(),
            response: JSON.stringify(error.response.data),
          };
        }

        if (_.get(error, 'response.config.data')) {
          request.payload = error.response.config.data;
        }

        Sentry.setContext('request', request);

        // changes the way the error is presented in sentry
        error.stack = `${error.config.method.toUpperCase()} ${ URL }`;
        error.message = error.toString();

        Sentry.captureException(error);
      });

      // Check for response for Forbidden and or Unauthorized codes to send user to login page
      if (error && error.response && unauthorizedStatus.includes(error.response.status)) {
        signOut();

        return redirectToLogin();
      }

      return Promise.reject(error);
    });

  return axios;
}

/**
 * Parses a url and replaces numbers between '/' for ':number' expression
 * Useful to identify possible dinamic ids in the urls strings
 *
 * @param url { string }
 */
function parseURL(url: string){
  return url.split('/')
    .map( part => Number(part) > 0 ? ':number' : part)
    .join('/')
    .toString();
}